import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../api";
import { ResultSheet } from "../components/ResultSheet";
import { Centered, Header, Shell } from "../components/Shell";
import { Toast } from "../components/Toast";
import { haptic } from "../telegram";
import { useGame } from "../useGame";

interface PubQuestion {
  t: string;
  img?: string;
  options: string[];
  started?: boolean;
  answer?: number | null;
  correct?: number;
  points?: number;
}

interface View {
  total: number;
  questions: (PubQuestion | null)[];
  answered: number;
  points: number;
}

interface AnsExtra {
  i: number;
  right: boolean;
  correct: number;
  points: number;
  ms: number;
}

const COPY = { not_started: "Спочатку відкрий питання", bad_request: "Щось не так" };

export function Kviz({ date }: { date?: string | undefined }) {
  const g = useGame<View>("kviz", COPY, date);
  // Post-answer flash: which option was mine, which was right.
  const [flash, setFlash] = useState<AnsExtra | null>(null);
  const startedFor = useRef<number | null>(null);

  const state = g.screen.kind === "game" ? g.screen.state : null;
  const finished = state !== null && state.status !== "playing";
  const current = useMemo(() => {
    const qs = state?.view.questions ?? [];
    const i = qs.findIndex((q) => q !== null && q.answer === undefined);
    return i >= 0 ? { i, q: qs[i] as PubQuestion } : null;
  }, [state?.view.questions]);

  // Arm the server clock the moment a fresh question is on screen.
  useEffect(() => {
    if (!current || finished || flash) return;
    if (startedFor.current === current.i) return;
    startedFor.current = current.i;
    void api.guess<View>("kviz", `start:${current.i}`, date).then((next) => g.apply(next)).catch(() => {
      startedFor.current = null;
    });
  }, [current, finished, flash, date, g]);

  const answer = async (j: number) => {
    if (!current || g.busy || flash) return;
    haptic.tap();
    const next = await g.run(() => api.guess<View>("kviz", `ans:${current.i}:${j}`, date));
    if (next) {
      const extra = next.extra as AnsExtra | undefined;
      if (extra) {
        setFlash(extra);
        window.setTimeout(() => setFlash(null), 1400);
      }
    }
  };

  if (g.screen.kind === "loading") return <Shell><Centered>…</Centered></Shell>;
  if (g.screen.kind === "error") {
    return (
      <Shell>
        <Centered>
          <div>{g.screen.message}</div>
          {g.screen.retry && (
            <button type="button" onClick={() => void g.load()} className="rounded-xl bg-tgbutton px-5 py-2.5 text-sm font-semibold text-tgbuttontext">
              ще раз
            </button>
          )}
        </Centered>
      </Shell>
    );
  }
  const s = g.screen.state;
  const v = s.view;
  const won = s.status === "won";
  const right = v.questions.filter((q) => q?.points && q.points > 0).length;

  // During the flash we still show the just-answered question with colours.
  const shownIdx = flash ? flash.i : current?.i;
  const shown = shownIdx !== undefined ? v.questions[shownIdx] : null;

  return (
    <Shell header={<Header left={date ? `📼 ${date}` : `#${s.number}`} title="КВІЗ" right={date ? "" : `🔥 ${s.me.streak}`} />}>
      <div className="flex flex-1 flex-col px-4 pb-3">
        <div className="mt-1 flex items-center justify-center gap-1.5">
          {Array.from({ length: v.total }, (_, i) => {
            const q = v.questions[i];
            const done = q?.answer !== undefined && q !== null;
            return (
              <span
                key={i}
                className={`h-2.5 w-2.5 rounded-full ${
                  done ? ((q?.points ?? 0) > 0 ? "bg-ok" : "bg-cold") : i === shownIdx ? "bg-tgbutton" : "bg-[rgba(128,128,128,0.25)]"
                }`}
              />
            );
          })}
        </div>
        <div className="mt-1 text-center text-xs text-tghint">
          балів: <b className="text-tgtext">{v.points}</b> · швидкість теж рахується ⚡
        </div>

        {!finished && shown && (
          <div className="flex flex-1 flex-col justify-center gap-3">
            <div className="rounded-2xl bg-tgsection px-5 py-5 text-center">
              <div className="text-[11px] text-tghint">питання {String((shownIdx ?? 0) + 1)}/{v.total}</div>
              <div className="mt-1 text-lg font-bold leading-snug">{shown.t}</div>
              {shown.img && (
                <img src={shown.img} alt="" className="mx-auto mt-3 h-20 rounded-md border border-[rgba(128,128,128,0.25)]" />
              )}
            </div>
            <div className="grid grid-cols-1 gap-2">
              {shown.options.map((o, j) => {
                const isMine = flash ? flash.i === shownIdx && j === (v.questions[flash.i]?.answer ?? -1) : false;
                const isRight = flash ? j === flash.correct : false;
                const tone = flash
                  ? isRight
                    ? "bg-ok/30"
                    : isMine
                      ? "bg-cold/30"
                      : "bg-tgsection opacity-60"
                  : "bg-tgsection active:opacity-70";
                return (
                  <button
                    key={j}
                    type="button"
                    disabled={g.busy || Boolean(flash)}
                    onClick={() => void answer(j)}
                    className={`rounded-xl px-4 py-3 text-left text-[15px] font-semibold ${tone}`}
                  >
                    {o}
                  </button>
                );
              })}
            </div>
            {flash && (
              <div className="text-center text-sm font-semibold">
                {flash.right ? `✅ +${flash.points} (${(flash.ms / 1000).toFixed(1)}с)` : "❌ 0 балів"}
              </div>
            )}
          </div>
        )}

        {finished && (
          <div className="flex-1 overflow-y-auto py-3">
            <ul className="space-y-1.5">
              {v.questions.map((q, i) =>
                q ? (
                  <li key={i} className="rounded-xl bg-tgsection px-3 py-2 text-sm">
                    <div className="flex items-center justify-between gap-2">
                      <span className="min-w-0 flex-1 truncate">{q.t}</span>
                      <span className={`shrink-0 font-semibold ${(q.points ?? 0) > 0 ? "text-ok" : "text-cold"}`}>
                        {(q.points ?? 0) > 0 ? `+${q.points}` : "0"}
                      </span>
                    </div>
                    <div className="mt-0.5 text-xs text-tghint">✔ {q.options[q.correct ?? 0]}</div>
                  </li>
                ) : null,
              )}
            </ul>
          </div>
        )}
      </div>

      <Toast message={g.toast} />
      {g.sheetOpen && finished && (
        <ResultSheet
          state={s}
          archive={Boolean(date)}
          title={right === v.total ? "Ерудит!" : right >= 3 ? "Непогано!" : "Буває"}
          subtitle={`⚡ ${v.points} балів (${right}/${v.total}) · стрік ${s.me.streak} 🔥`}
          scoreLabel="avg бали"
          formatScore={(n) => (n === null ? "–" : `${1000 - n}`)}
          onDismiss={() => g.setSheetOpen(false)}
        />
      )}
    </Shell>
  );
}
